import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import type { FilesHubBrowseResult } from "@/api/files-hub/filesHub.queries";

export const FileSharesQueryKeys = {
  shares: "fileShares",
  recipients: "fileShareRecipients",
  sharedWithMe: "filesSharedWithMe",
  sharedByMe: "filesSharedByMe",
  entityRecords: "fileShareEntityRecords",
  link: "fileShareLink",
  linkResolve: "fileShareLinkResolve",
  linkRecords: "fileShareLinkRecords",
} as const;

export type FileSharePermission = "VIEW" | "EDIT";

export type ShareRecordType =
  | "entities"
  | "clients"
  | "work-orders"
  | "suppliers"
  | "service-providers";

export type EntityShareRecordType = "clients" | "work-orders";

export interface EntityShareRecord {
  type: EntityShareRecordType;
  id: string;
}

export type ShareTarget =
  | { kind: "item"; fileId: string; ownDrive?: boolean }
  | { kind: "record"; recordType: ShareRecordType; recordId: string }
  | { kind: "sp_document"; documentId: string };

export interface FileShareUser {
  id: string;
  name: string;
  email: string;
}

export interface FileShare {
  id: string;
  permission: FileSharePermission;
  createdAt: string;
  user: FileShareUser;
}

export interface FileSharesResult {
  viewOnly: boolean;
  items: FileShare[];
}

export type SharedWithMeTarget =
  | { kind: "item" }
  | { kind: "sp_document" }
  | {
      kind: "record";
      recordType: "entity" | "client" | "work_order" | "supplier" | "service_provider";
    };

export interface SharedWithMeItem {
  shareId: string;
  permission: FileSharePermission;
  sharedAt: string;
  sharedBy: { id: string; name: string };
  target: SharedWithMeTarget;
  item: {
    id: string;
    name: string;
    type: "FOLDER" | "FILE";
    contentType: string | null;
    fileSize: number | null;
    createdAt: string;
    updatedAt: string;
  };
}

export type ShareLinkAudience = "RESTRICTED" | "ENTITY" | "ENTITY_ROLES" | "CLIENT_TEAM";

export interface ShareLinkOptions {
  audiences: ShareLinkAudience[];
  editAudiences: ShareLinkAudience[];
}

export interface ShareLinkPayload {
  audience: ShareLinkAudience;
  roleIds?: string[];
  permission?: FileSharePermission;
}

export interface ShareLink {
  id: string;
  audience: ShareLinkAudience;
  roleIds: string[];
  permission: FileSharePermission;
  url: string;
  createdAt: string;
  createdBy: { id: string; name: string };
}

export interface ResolvedShareLink {
  itemId: string | null;
  name: string;
  itemType: "FOLDER" | "FILE";
  targetKind: "item" | "record" | "sp_document";
  recordType?: "entity" | "client" | "work_order" | "supplier" | "service_provider";
  permission: FileSharePermission;
  sharedBy: { id: string; name: string };
  entityName: string;
}

export interface SharedByMeItem {
  target:
    | { kind: "item"; fileId: string }
    | { kind: "record"; recordType: ShareRecordType; recordId: string }
    | { kind: "sp_document"; documentId: string };
  ownDrive: boolean;
  item: SharedWithMeItem["item"];
  recipients: {
    shareId: string;
    permission: FileSharePermission;
    sharedAt: string;
    user: FileShareUser;
  }[];
  link: {
    audience: ShareLinkAudience;
    url: string;
    roleIds: string[];
    permission: FileSharePermission;
  } | null;
}

export function shareTargetKey(target: ShareTarget): string {
  switch (target.kind) {
    case "item":
      return target.ownDrive ? `item:own:${target.fileId}` : `item:${target.fileId}`;
    case "record":
      return `record:${target.recordType}:${target.recordId}`;
    case "sp_document":
      return `sp_document:${target.documentId}`;
  }
}

function sharesPath(target: ShareTarget): string {
  switch (target.kind) {
    case "item":
      return target.ownDrive
        ? `/files/user/items/${target.fileId}/shares`
        : `/files/items/${target.fileId}/shares`;
    case "record":
      return `/files/records/${target.recordType}/${target.recordId}/shares`;
    case "sp_document":
      return `/files/sp-documents/${target.documentId}/shares`;
  }
}

function targetQuery(target: ShareTarget): Record<string, string> {
  switch (target.kind) {
    case "item":
      return { fileId: target.fileId };
    case "record":
      return { recordType: target.recordType, recordId: target.recordId };
    case "sp_document":
      return { spDocumentId: target.documentId };
  }
}

export const listShares = async (target: ShareTarget): Promise<FileSharesResult> => {
  const { data } = await axiosInstance.get(sharesPath(target));
  return data.data;
};

export const shareWithPeople = async (
  target: ShareTarget,
  payload: { userIds: string[]; permission: FileSharePermission },
): Promise<FileSharesResult> => {
  const { data } = await axiosInstance.put(sharesPath(target), payload, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return data.data;
};

export const revokeShare = async (target: ShareTarget, shareId: string): Promise<void> => {
  await axiosInstance.delete(`${sharesPath(target)}/${shareId}`, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
};

export const searchShareRecipients = async (
  target: ShareTarget,
  search: string,
  signal?: AbortSignal,
): Promise<FileShareUser[]> => {
  const params: Record<string, string> = targetQuery(target);
  if (search) params.search = search;
  const { data } = await axiosInstance.get("/files/share-recipients", { params, signal });
  return data.data.items;
};

export const getSharedWithMe = async (signal?: AbortSignal): Promise<SharedWithMeItem[]> => {
  const { data } = await axiosInstance.get("/files/shared-with-me", { signal });
  return data.data.items;
};

export const getSharedByMe = async (signal?: AbortSignal): Promise<SharedByMeItem[]> => {
  const { data } = await axiosInstance.get("/files/shared-by-me", { signal });
  return data.data.items;
};

/**
 * A short-lived link to one file reached through a share, minted on demand: share listings
 * carry no S3 key or signed URL, so nothing reusable sits in the page or its cache.
 */
export const getShareDownloadUrl = async (
  shareId: string,
  itemId: string,
  record?: EntityShareRecord,
): Promise<string> => {
  const path = record
    ? `/files/shared-with-me/${shareId}/records/${record.type}/${record.id}/download`
    : `/files/shared-with-me/${shareId}/download`;
  const { data } = await axiosInstance.get(path, { params: { itemId } });
  return data.data.url;
};

export const getEntityShareRecords = async (
  shareId: string,
  type: EntityShareRecordType,
  options: { page: number; limit: number; search?: string },
  signal?: AbortSignal,
): Promise<FilesHubBrowseResult> => {
  const params: Record<string, string> = {
    type,
    page: String(options.page),
    limit: String(options.limit),
  };
  if (options.search) params.search = options.search;
  const { data } = await axiosInstance.get(`/files/shared-with-me/${shareId}/records`, {
    params,
    signal,
  });
  return data.data;
};

/** The URL a `file_share` notification opens: absolute, on the owner entity's host. */
export const getShareOpenUrl = async (shareId: string): Promise<string> => {
  const { data } = await axiosInstance.get(`/files/shares/${shareId}/open-url`);
  return data.data.url;
};

const linkPath = (target: ShareTarget) => sharesPath(target).replace(/\/shares$/, "/link");

export const getShareLink = async (target: ShareTarget): Promise<ShareLink | null> => {
  const { data } = await axiosInstance.get(linkPath(target));
  return data.data;
};

export const getShareLinkOptions = async (target: ShareTarget): Promise<ShareLinkOptions> => {
  const { data } = await axiosInstance.get(`${linkPath(target)}/options`);
  return data.data;
};

export const setShareLink = async (
  target: ShareTarget,
  payload: ShareLinkPayload,
): Promise<ShareLink> => {
  const { data } = await axiosInstance.put(linkPath(target), payload, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return data.data;
};

export const resetShareLink = async (target: ShareTarget): Promise<ShareLink> => {
  const { data } = await axiosInstance.post(`${linkPath(target)}/reset`, undefined, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return data.data;
};

export const deleteShareLink = async (target: ShareTarget): Promise<void> => {
  await axiosInstance.delete(linkPath(target), {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
};

/** The link a Copy link action needs: the live one, or a Restricted link created on first use. */
export const ensureShareLink = async (target: ShareTarget): Promise<ShareLink> =>
  (await getShareLink(target)) ?? setShareLink(target, { audience: "RESTRICTED" });

export const linkApiPath = (token: string, ...rest: string[]) =>
  ["/files/links", encodeURIComponent(token), ...rest].join("/");

export const resolveShareLink = async (
  token: string,
  signal?: AbortSignal,
): Promise<ResolvedShareLink> => {
  const { data } = await axiosInstance.get(linkApiPath(token), {
    signal,
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return data.data;
};

export const getLinkDownloadUrl = async (
  token: string,
  itemId: string,
  record?: EntityShareRecord,
): Promise<string> => {
  const path = record
    ? linkApiPath(token, "records", record.type, record.id, "download")
    : linkApiPath(token, "download");
  const { data } = await axiosInstance.get(path, { params: { itemId } });
  return data.data.url;
};

export const getLinkEntityRecords = async (
  token: string,
  type: EntityShareRecordType,
  options: { page: number; limit: number; search?: string },
  signal?: AbortSignal,
): Promise<FilesHubBrowseResult> => {
  const params: Record<string, string> = {
    type,
    page: String(options.page),
    limit: String(options.limit),
  };
  if (options.search) params.search = options.search;
  const { data } = await axiosInstance.get(linkApiPath(token, "records"), {
    params,
    signal,
  });
  return data.data;
};
